import { useEffect, useState } from 'react';
import { SandhillsSlickSlider, Panel } from '@Components/Shared/SandhillsSlickSlider';
import { sandhillsRequest } from '@Components/Utilities/SandhillsUtility';
import { useWindowSize } from '@Components/Utilities/WindowSize';
import { LazyLoadImage } from 'react-lazy-load-image-component';
import atoms from '@Components/Utilities/SandhillsAtoms';
import TranslationText from '@Components/Utilities/TranslationText';
import { useRecoilValue } from 'recoil';
import ready from 'document-ready';

const ListingCarousel = (props) => {
    const [userWatchlist, setUserWatchlist] = useState([]);
    const currentUser = useRecoilValue(atoms.currentUser);
    const currentSite = useRecoilValue(atoms.currentWebsite);
    const routes = useRecoilValue(atoms.routes);
    const windowSize = useWindowSize();
    const initialSliderCount = windowSize?.width < 768 ? 2 : windowSize?.width < 1000 ? 3 : 4;

    const isRecommendedListingsPanel = props.ChildComponent == 'RecommendedListingsPanel';
    const isAuctionsForYouPanel = props.ChildComponent == 'AuctionsForYouPanel';

    useEffect(() => {
        if (isRecommendedListingsPanel && !currentSite?.Site?.IsHostedSite) {
            getUserWatchlist();

            // Initalize recommended listings image heights for mobile
            ready(() => {
                setRecommendedListingsCarouselImageStyles();
            });

            // Screen resize event listener for dynamic resizing 
            const handleResize = () => {
                setRecommendedListingsCarouselImageStyles();
            };

            window.addEventListener('resize', handleResize);

            return () => {
                window.removeEventListener('resize', handleResize);
            };
        }
    }, []);

    const getUserWatchlist = () => {
        if (currentUser.IsAuthenticated && !currentSite?.Site?.IsHostedSite && props?.Cards?.length > 0) {
            sandhillsRequest({
                url: routes.GetWatchList.replace("{0}", ""),
                errorHandling: {
                    message: 'Error retrieving watchlist',
                    showDetails: true,
                    useConsole: true,
                    useAlert: false
                }
            }).then((response) => {
                if (response && response.length > 0) {
                    setUserWatchlist(response);
                }
            });
        }
    };

    const setRecommendedListingsCarouselImageStyles = () => {
        if (window.innerWidth <= 480) {
            document.querySelectorAll('.recommendedlistingsslider .listing').forEach(listing => {
                if (listing.querySelector('.listing-image-footer')?.clientHeight > 30) {
                    listing.querySelector('.listing-image-blur').style.height = '86px';
                } else if (listing.querySelector('.listing-image-blur')) {
                    listing.querySelector('.listing-image-blur').style.height = '101px';
                }
            });
        } else {
            document.querySelectorAll('.recommendedlistingsslider .listing .listing-image-blur').forEach(listing => {
                listing.style.height = '';
            });
        }
    };

    return (
        <>
            {props?.Cards?.length > 0 &&
                <>
                    {(isRecommendedListingsPanel || isAuctionsForYouPanel) &&
                        <div className='listing-carousel' id='listing-carousel'>
                            <SandhillsSlickSlider
                                ShowSection
                                {...props}
                                settings={{
                                    slidesToShow: initialSliderCount,
                                    slidesToScroll: initialSliderCount,
                                    responsive: [
                                        {
                                            breakpoint: 1000,
                                            settings: {
                                                slidesToShow: 3,
                                                slidesToScroll: 3
                                            }
                                        },
                                        {
                                            breakpoint: 768,
                                            settings: {
                                                slidesToShow: 2,
                                                slidesToScroll: 2
                                            }
                                        }
                                    ]
                                }}
                                showSectionTitle={true}
                            >
                                {props.Cards.map(card =>
                                    <Panel
                                        key={`slider-${card.Url}`}
                                        CustomWatchlists={props.CustomWatchlists}
                                        getUserWatchlist={getUserWatchlist}
                                        PanelType={props.ChildComponent}
                                        PanelClass={props.PanelClass}
                                        setUserWatchlist={setUserWatchlist}
                                        ShowEvaluatorValues={props.ShowEvaluatorValues}
                                        UserWatchlist={userWatchlist}
                                        {...card}
                                    />)}
                            </SandhillsSlickSlider>
                        </div>
                    }
                    {!(isRecommendedListingsPanel || isAuctionsForYouPanel) && !windowSize.isMobile && 
                        <div className='listing-carousel'>
                            <SandhillsSlickSlider
                                ShowSection
                                {...props}
                                settings={{
                                    slidesToShow: 4,
                                    slidesToScroll: 4,
                                    responsive: [
                                        {
                                            breakpoint: 1430,
                                            settings: {
                                                slidesToShow: 4,
                                                slidesToScroll: 4
                                            }
                                        }
                                    ]
                                }}
                                showSectionTitle={true}
                            >
                                {props.Cards.map(card =>
                                    <Panel key={`slider-${card.Url}`} PanelType={props.ChildComponent} PanelClass={props.PanelClass} {...card} />)}
                            </SandhillsSlickSlider>
                        </div>
                    }
                    {!(isRecommendedListingsPanel || isAuctionsForYouPanel) && windowSize.isMobile &&
                        <div className='listing-carousel'>
                            <section className='latest-listings-section'>
                                <h2 className='section-title'>
                                    <span className='section-title-text'>
                                        {props.translationKey ? <TranslationText tKey={props.translationKey} /> : props.Title}
                                    </span>
                                    {props.LogoURL &&
                                        <span>
                                            <br />
                                            <LazyLoadImage src={props.LogoURL} alt='' className='wordmark' />
                                        </span>}
                                </h2>
                                {props.Cards.slice(0, 4).map(listing =>
                                    <div key={`mobile-${listing.Url}`} className='listing'>
                                        <div className='listing-content'>
                                            <a aria-label={listing.ListingTitle} href={listing.Url} className='latest-listing-container-link'>
                                                <LazyLoadImage src={listing.ImgUrl} className="listing-image" alt='' />
                                            </a>
                                            <div className='name-and-category'>
                                                <a href={listing.Url} className='listing-title-link'>
                                                    <span className='listing-name'>{listing.ListingTitle}</span>
                                                    <span className='listing-category'>{listing.SecondaryText}</span>
                                                </a>
                                                <div>
                                                    {listing.PriceLabel && <span className='listing-price-label'>{listing.PriceLabel}:&nbsp;</span>}
                                                    <span className='listing-price'>{listing.TierchiaryText}</span>
                                                </div>
                                                <a className='view-listing-details-link des-view-listingDetails' href={listing.Url}>
                                                    <strong><TranslationText tKey='sandhills.tradepubwebsite.mvc.viewdetails' /></strong>
                                                </a>
                                            </div>
                                        </div>
                                    </div>)}
                                <div className='va-container'>
                                    <a href={props.ViewAllLink} className='button ts-button'>
                                        <TranslationText tKey='sandhills.tradepubwebsite.mvc.viewall' />
                                    </a>
                                </div>
                            </section>
                        </div>
                    }
                </>
            }
        </>
    );
};

export default ListingCarousel;